import { BORDER_RADIUSES, COLORS } from 'shared/styles/variables';
import styled from 'styled-components';

import IdBarcodeGenericGuide from 'images/pictographs/barcode-generic-guide.svg';
import ImageFrame from 'images/verify/frame.svg';
import { GovernmentIdClass } from 'lib/constants/governmentIds';
import { GovernmentIdSide } from 'lib/interfaces/verificationConfig';
import IdBackGuide from 'verify/components/ScannerOverlay/IdBackGuide';
import IdFrontGuide from 'verify/components/ScannerOverlay/IdFrontGuide';
import MrzTwoLineGuide from 'verify/components/ScannerOverlay/MrzTwoLineGuide';

const CropPositioner = styled('div')`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;

const OverlayCrop = styled('div')`
  position: relative;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

interface ScannerOverlayProps {
  governmentIdClass: GovernmentIdClass;
  requiresSide: GovernmentIdSide | undefined;
  mirrored: boolean;
  // Configure whether to use the simple default overlay.
  simple?: boolean;
  // Configure whether or not to hide back overlay
  hideBackOverlay?: boolean;
}

const ScannerOverlay = (props: ScannerOverlayProps) => {
  return (
    <CropPositioner>
      <OverlayCrop>
        <InnerOverlay {...props} />
      </OverlayCrop>
    </CropPositioner>
  );
};

const OverlayBarcode = styled('div')`
  position: absolute;
  margin: 30% 20%;
  padding-bottom: 17%;
  width: 60%;
`;

const ImageStyled = styled('div')`
  svg {
    position: absolute;
    width: 100%;
    height: 100%;
  }
`;

const SimpleGuide = styled('div')`
  padding-bottom: 65.97%;
  width: 100%;
`;

const IdBarcodeGenericGuideStyled = styled(IdBarcodeGenericGuide)`
  position: absolute;
  width: 100%;
  height: 100%;
`;

const OverlayWhole = styled('div')`
  position: absolute;
  border-radius: ${BORDER_RADIUSES.base};
  border: 2px dashed ${COLORS.white};

  margin: 13% 10%;
  padding-bottom: 48%;
  width: 80%;
  box-shadow: 0 0 1px 1000px rgba(0, 0, 0, 0.5);
`;

const OverlayPortrait = styled('div')`
  position: absolute;
  border-radius: ${BORDER_RADIUSES.base};
  border: 2px dashed ${COLORS.white};

  margin: 10% 10%;
  width: 80%;
  box-shadow: 0 0 1px 1000px rgba(0, 0, 0, 0.5);
`;

const OverlayMrzTwoLine = styled('div')`
  position: absolute;
  border-radius: ${BORDER_RADIUSES.base};
  border: 2px dashed ${COLORS.white};

  margin: 12% 10% 8% 10%;
  width: 80%;
  box-shadow: 0 0 1px 1000px rgba(0, 0, 0, 0.7);
`;

const InnerOverlay = (props: ScannerOverlayProps) => {
  if (props.governmentIdClass == null || props.simple) {
    // Don't show any specific overlay for unknown IDs.
    return <OverlayWhole />;
  } else if (props.requiresSide === GovernmentIdSide.PassportSignature) {
    return <OverlayWhole />;
  } else if (['nric'].includes(props.governmentIdClass)) {
    // https://www.notion.so/withpersona/START-HERE-Malacca-Update-format-of-GovID-overlay-guidance-576ace2518f64284bf3fccf94e01b760
    return <OverlayWhole />;
  } else if (['pp', 'visa', 'ipp'].includes(props.governmentIdClass)) {
    // potentially migrate this such that pp takes MRZ_FRONT as a side instead
    return (
      <OverlayMrzTwoLine>
        <MrzTwoLineGuide mirrored={props.mirrored} />
      </OverlayMrzTwoLine>
    );
  } else if (props.requiresSide === GovernmentIdSide.Front) {
    return (
      <OverlayPortrait>
        <IdFrontGuide mirrored={props.mirrored} />
      </OverlayPortrait>
    );
  } else if (props.requiresSide === GovernmentIdSide.Back) {
    return (
      <OverlayPortrait>{props.hideBackOverlay ? <SimpleGuide /> : <IdBackGuide />}</OverlayPortrait>
    );
  } else if (props.requiresSide === GovernmentIdSide.BarcodePdf417) {
    return (
      <OverlayBarcode>
        <ImageStyled>
          <ImageFrame />
        </ImageStyled>
        <IdBarcodeGenericGuideStyled />
      </OverlayBarcode>
    );
  } else {
    return <OverlayWhole />;
  }
};

export default ScannerOverlay;
